// frontend/hooks/useMultiplayer.ts
// Multiplayer hook using Socket.IO (server-authoritative)

import { useState, useEffect, useCallback, useRef } from 'react';
import { getSocket, disconnectSocket, getCurrentSocket } from '../utils/socket';
import { sounds } from '../sounds';
import type { Socket } from 'socket.io-client';
import type { GameMode, SpecialDiceType } from '../types/game';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GameState {
  playerGrid: (number | null)[][];
  opponentGrid: (number | null)[][];
  currentDie: number | SpecialDiceType;
  currentPlayer: 'player1' | 'player2';
  playerScore: number;
  opponentScore: number;
  gameStatus: 'waiting' | 'countdown' | 'rolling' | 'placing' | 'thinking' | 'game_over';
  gameMode: GameMode;
  message: string;
  lastAction: { col: number; target: 'player' | 'opponent' } | null;
  countdown?: number;
  player1Id?: string;
  player2Id?: string;
  winner?: string | null;
  shields: { player1: { [col: number]: number }; player2: { [col: number]: number } };
  currentSpecialDie: SpecialDiceType | null;
  rerollPhase: number;
}

export interface Player {
  id: string;
  name: string;
  playerNumber: 1 | 2;
  ready: boolean;
  level?: number;
}

export interface ChatMessage {
  id: string;
  playerId: string;
  playerName: string;
  text: string;
  timestamp: number;
  isMine: boolean;
}

// ─── Audio (countdown beeps) ─────────────────────────────────────────────────

let audioContext: AudioContext | null = null;

function initAudio() {
  if (!audioContext && typeof window !== 'undefined') {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioContext = new AudioCtx();
  }
}

function playCountdownSound(count: number) {
  if (!audioContext) return;
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  const frequencies: Record<number, number> = { 3: 880, 2: 660, 1: 550, 0: 1100 };
  oscillator.frequency.value = frequencies[count] || 440;
  oscillator.type = 'sine';
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.3);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useMultiplayer = (
  roomId: string | null,
  playerId: string | null,
  playerName: string,
  isMuted: boolean = false
) => {
  // ── Sound helper ────────────────────────────────────────────────────────
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;

  const playSound = useCallback((key: keyof typeof sounds) => {
    if (!isMutedRef.current) {
      sounds[key]();
    }
  }, []);

  const [isConnected, setIsConnected] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerNumber, setPlayerNumber] = useState<number>(0);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdownNumber, setCountdownNumber] = useState<number | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isDieRolling, setIsDieRolling] = useState(false);
  const [turnTimer, setTurnTimer] = useState<number | null>(null);
  const [turnTimerMax, setTurnTimerMax] = useState<number>(5);
  const [rematchState, setRematchState] = useState<'none' | 'sent' | 'received' | 'accepted' | 'declined'>('none');
  const [joinedPlayerId, setJoinedPlayerId] = useState<string | null>(null);
  const [roomExpired, setRoomExpired] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const lastCountdownRef = useRef<number | null>(null);
  const gameOverSoundPlayedRef = useRef(false);
  const prevPlayerCountRef = useRef<number>(0);

  // ── Cleanup ──────────────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    const sock = socketRef.current;
    if (sock) {
      sock.off(); // remove all listeners
      sock.emit('leave_room');
    }
    socketRef.current = null;
    prevPlayerCountRef.current = 0;
    setIsConnected(false);
  }, []);

  // ── Connect to Socket.IO server & bind listeners ─────────────────────────

  useEffect(() => {
    if (!roomId) return;

    console.log('[Multiplayer] Connecting to game server...', { roomId, playerId });
    setIsLoading(true);
    setError(null);
    initAudio();

    let cancelled = false;

    (async () => {
      try {
        const sock = await getSocket();
        if (cancelled) return;

        socketRef.current = sock;
        setIsConnected(sock.connected);

        // ── Server -> Client event listeners ──────────────────────────────

        // Room state updates (players list, room status)
        sock.on('room_state', (data: { players: Player[]; status: string; roomId: string }) => {
          console.log('[Multiplayer] room_state:', data);

          // Play join sound when a new player enters the room
          if (data.players.length > prevPlayerCountRef.current && prevPlayerCountRef.current > 0) {
            playSound('join');
          }
          prevPlayerCountRef.current = data.players.length;

          setPlayers(data.players);

          // Find my player number
          const me = data.players.find((p) => p.id === playerId);
          if (me) {
            setPlayerNumber(me.playerNumber);
          }

          // If all players have ready=false, the room was reset (rematch or reconnect)
          // Clear game state so we go back to the waiting/ready screen
          const allNotReady = data.players.length > 0 && data.players.every((p) => !p.ready);
          if (allNotReady) {
            setGameState(null);
            setRematchState('none');
            setCountdownNumber(null);
            setIsDieRolling(false);
            setTurnTimer(null);
            setChatMessages([]);
            gameOverSoundPlayedRef.current = false;
            lastCountdownRef.current = null;
          }
        });

        // Countdown
        sock.on('countdown', (data: { count: number }) => {
          console.log('[Multiplayer] countdown:', data.count);
          setCountdownNumber(data.count);

          if (data.count !== lastCountdownRef.current) {
            lastCountdownRef.current = data.count;
            playCountdownSound(data.count);
            if (data.count === 0) {
              setTimeout(() => setCountdownNumber(null), 1500);
            }
          }
        });

        // Game state update (authoritative from server)
        sock.on('game_state', (state: GameState) => {
          console.log('[Multiplayer] game_state:', state.gameStatus);
          setIsDieRolling(false);
          setGameState(state);
          setCountdownNumber(null);
          lastCountdownRef.current = null;
          setRematchState((prev) => prev === 'none' ? 'none' : prev);

          // Reset turn timer when a new roll phase starts (prevents carry-over)
          if (state.gameStatus === 'rolling') {
            setTurnTimer(null);
          }
        });

        // Dice rolling animation
        sock.on('dice_rolling', (data: { player: string; value: number | SpecialDiceType }) => {
          console.log('[Multiplayer] dice_rolling:', data);
          setIsDieRolling(true);
          if (typeof data.value === 'string') {
            playSound('specialRoll');
          } else {
            playSound('roll');
          }
        });

        // Turn timer
        sock.on('turn_timer', (data: { remaining: number }) => {
          setTurnTimer(prev => {
            // First tick of a new turn — capture the max value
            if (prev === null || data.remaining > prev) {
              setTurnTimerMax(data.remaining);
            }
            return data.remaining;
          });

          // Play tick sounds when it's my turn
          setGameState((gs) => {
            if (!gs || gs.gameStatus !== 'placing') return gs;

            const isMyTurn =
              (gs.currentPlayer === 'player1' && playerNumber === 1) ||
              (gs.currentPlayer === 'player2' && playerNumber === 2);

            if (isMyTurn) {
              if (data.remaining === 1) {
                playSound('tickUrgent');
              } else if (data.remaining === 0) {
                playSound('timeout');
              } else if (data.remaining > 0) {
                playSound('tick');
              }
            }
            return gs; // don't modify state
          });
        });

        // Game over
        sock.on('game_over', (data: { winner: string | null; finalState: GameState }) => {
          console.log('[Multiplayer] game_over:', data);
          setIsDieRolling(false);
          setGameState(data.finalState);
          setTurnTimer(null);
        });

        // Opponent disconnected
        sock.on('opponent_disconnected', (data: { message: string }) => {
          console.log('[Multiplayer] opponent_disconnected:', data.message);
          // Disable rematch since opponent is gone
          setRematchState('declined');
        });

        // Chat
        sock.on('chat_message', (data: { playerId: string; playerName: string; text: string; timestamp: number }) => {
          console.log('[Multiplayer] chat_message:', data);
          const msg: ChatMessage = {
            id: `${data.timestamp}-${data.playerId}`,
            playerId: data.playerId,
            playerName: data.playerName,
            text: data.text,
            timestamp: data.timestamp,
            isMine: data.playerId === playerId,
          };
          setChatMessages((prev) => [...prev, msg]);
        });

        // Rematch events
        sock.on('rematch_request', () => {
          console.log('[Multiplayer] rematch_request received');
          setRematchState('received');
        });

        sock.on('rematch_accept', () => {
          console.log('[Multiplayer] rematch_accept received');
          setRematchState('accepted');
        });

        sock.on('rematch_decline', () => {
          console.log('[Multiplayer] rematch_decline received');
          setRematchState('declined');
        });

        // Waiting for opponent (quick match)
        sock.on('waiting_for_opponent', () => {
          console.log('[Multiplayer] waiting_for_opponent');
        });

        // Room expired (idle timeout)
        sock.on('room_expired', (data: { message: string }) => {
          console.log('[Multiplayer] room_expired:', data.message);
          setRoomExpired(data.message);
        });

        // Error
        sock.on('error', (data: { message: string }) => {
          console.error('[Multiplayer] error:', data.message);
          setError(data.message);
        });

        // Connection state
        sock.on('connect', () => setIsConnected(true));
        sock.on('disconnect', () => setIsConnected(false));
        sock.on('reconnect', () => {
          console.log('[Multiplayer] Reconnected, re-joining room...');
          setIsConnected(true);
          // Re-join the room after reconnect
          sock.emit('join_room', { roomId }, (res: { success: boolean; playerNumber?: number }) => {
            if (res.success && res.playerNumber) {
              setPlayerNumber(res.playerNumber);
            }
          });
        });

        // ── Join the room ────────────────────────────────────────────────

        sock.emit('join_room', { roomId }, (res: { success: boolean; playerId?: string; playerNumber?: number; error?: string }) => {
          if (cancelled) return;
          setIsLoading(false);

          if (res.success) {
            console.log('[Multiplayer] Joined room successfully, player #', res.playerNumber, 'playerId:', res.playerId);
            if (res.playerNumber) {
              setPlayerNumber(res.playerNumber);
            }
            if (res.playerId) {
              setJoinedPlayerId(res.playerId);
            }
          } else {
            console.error('[Multiplayer] Failed to join room:', res.error);
            setError(res.error || 'Failed to join room');
          }
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[Multiplayer] Connection failed:', err);
        setError((err as Error).message);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      console.log('[Multiplayer] Cleaning up...');
      const sock = socketRef.current;
      if (sock) {
        sock.off('room_state');
        sock.off('countdown');
        sock.off('game_state');
        sock.off('dice_rolling');
        sock.off('turn_timer');
        sock.off('game_over');
        sock.off('opponent_disconnected');
        sock.off('chat_message');
        sock.off('rematch_request');
        sock.off('rematch_accept');
        sock.off('rematch_decline');
        sock.off('waiting_for_opponent');
        sock.off('room_expired');
        sock.off('error');
        sock.off('reconnect');
        sock.emit('leave_room');
      }
    };
  }, [roomId]);

  // ── Win/Lose sound on game over ──────────────────────────────────────────
  useEffect(() => {
    if (!gameState || gameState.gameStatus !== 'game_over') {
      gameOverSoundPlayedRef.current = false;
      return;
    }
    if (gameOverSoundPlayedRef.current) return;
    gameOverSoundPlayedRef.current = true;

    const myPlayerKey = playerNumber === 1 ? 'player1' : 'player2';
    if (gameState.winner === myPlayerKey) {
      playSound('win');
    } else if (gameState.winner === null) {
      // tie - no specific sound
    } else {
      playSound('lose');
    }
  }, [gameState?.gameStatus, gameState?.winner, playerNumber, playSound]);

  // ── Place sound when state changes to 'placing' or has lastAction ────────
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!gameState) return;

    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = gameState.gameStatus;

    // Play placement sound when transitioning from rolling/placing to rolling (opponent placed)
    if (prevStatus === 'rolling' && gameState.gameStatus === 'rolling') {
      // This means a placement just happened (server cycles: rolling -> placing -> rolling)
      playSound('place');
    }

    // Play destroy sound if there's a lastAction targeting us
    if (gameState.lastAction) {
      const myTarget = playerNumber === 1 ? 'player' : 'opponent';
      if (gameState.lastAction.target === myTarget) {
        playSound('destroy');
      } else {
        playSound('place');
      }
    }
  }, [gameState?.playerScore, gameState?.opponentScore, gameState?.lastAction]);

  // ── Actions ────────────────────────────────────────────────────────────

  const rollDice = useCallback(async () => {
    // no-op: server handles rolling automatically
  }, []);

  const placeDice = useCallback(
    async (colIndex: number) => {
      const sock = socketRef.current;
      if (!sock || !gameState) return;
      if (gameState.gameStatus !== 'placing' || !gameState.currentDie) return;

      const isMyTurn =
        (gameState.currentPlayer === 'player1' && playerNumber === 1) ||
        (gameState.currentPlayer === 'player2' && playerNumber === 2);

      if (!isMyTurn) {
        console.log('[Multiplayer] Not my turn, ignoring place');
        return;
      }

      if (gameState.currentDie === 'bomb') playSound('bomb');

      console.log('[Multiplayer] Placing dice at column:', colIndex);
      sock.emit('place_dice', { col: colIndex });
    },
    [gameState, playerNumber]
  );

  const setReady = useCallback(async () => {
    const sock = socketRef.current;
    if (!sock) return;
    console.log('[Multiplayer] Toggling ready state...');
    sock.emit('player_ready');
  }, []);

  const selectJokerValue = useCallback(async (value: number) => {
    const sock = socketRef.current;
    if (!sock) return;
    playSound('joker');
    console.log('[Multiplayer] Sending joker value:', value);
    sock.emit('select_joker_value', { value });
  }, [playSound]);

  const selectShieldColumn = useCallback(async (colIndex: number) => {
    const sock = socketRef.current;
    if (!sock) return;
    playSound('shield');
    console.log('[Multiplayer] Selecting shield column:', colIndex);
    sock.emit('select_shield_column', { col: colIndex });
  }, [playSound]);

  const sendChat = useCallback(
    async (text: string) => {
      const sock = socketRef.current;
      if (!sock || !text.trim()) return;
      sock.emit('chat', { text: text.trim().slice(0, 500) });
    },
    []
  );

  // ── Rematch actions ──────────────────────────────────────────────────────

  const requestRematch = useCallback(async () => {
    const sock = socketRef.current;
    if (!sock) return;
    console.log('[Multiplayer] Requesting rematch...');
    setRematchState('sent');
    sock.emit('rematch_request');
  }, []);

  const acceptRematch = useCallback(async () => {
    const sock = socketRef.current;
    if (!sock) return;
    console.log('[Multiplayer] Accepting rematch...');
    setRematchState('accepted');
    sock.emit('rematch_accept');
  }, []);

  const declineRematch = useCallback(async () => {
    const sock = socketRef.current;
    if (!sock) return;
    console.log('[Multiplayer] Declining rematch...');
    setRematchState('declined');
    sock.emit('rematch_decline');
  }, []);

  const disconnect = useCallback(() => {
    const sock = socketRef.current;
    if (sock) {
      sock.emit('leave_room');
      // Remove listeners but keep connection alive for reuse
      sock.off('room_state');
      sock.off('countdown');
      sock.off('game_state');
      sock.off('dice_rolling');
      sock.off('turn_timer');
      sock.off('game_over');
      sock.off('opponent_disconnected');
      sock.off('chat_message');
      sock.off('rematch_request');
      sock.off('rematch_accept');
      sock.off('rematch_decline');
      sock.off('waiting_for_opponent');
      sock.off('room_expired');
      sock.off('error');
    }
    socketRef.current = null;
    setPlayers([]);
    setPlayerNumber(0);
    setGameState(null);
    setError(null);
    setCountdownNumber(null);
    setChatMessages([]);
    setRematchState('none');
    setRoomExpired(null);
    setIsDieRolling(false);
    setTurnTimer(null);
    setIsConnected(false);
  }, []);

  return {
    isConnected,
    gameState,
    players,
    playerNumber,
    joinedPlayerId,
    error,
    isLoading,
    isDieRolling,
    turnTimer,
    turnTimerMax,
    countdownNumber,
    chatMessages,
    rematchState,
    roomExpired,
    rollDice,
    placeDice,
    setReady,
    sendChat,
    requestRematch,
    acceptRematch,
    declineRematch,
    disconnect,
    selectJokerValue,
    selectShieldColumn,
  };
};

// ─── REST-like API helpers (now via Socket.IO) ──────────────────────────────────

interface CreateRoomResponse {
  roomId: string;
  playerId: string;
}

interface JoinRoomResponse {
  roomId: string;
  playerId: string;
  playerNumber: number;
}

interface RoomData {
  id: string;
  status: string;
  difficulty: string;
  room_participants: { player_id: string; player_name: string; player_number: number; ready: boolean; room_id: string }[];
}

export const api = {
  async createRoom(difficulty?: string, gameMode?: GameMode): Promise<CreateRoomResponse> {
    const sock = await getSocket();
    return new Promise((resolve, reject) => {
      sock.emit('create_room', { difficulty: difficulty || 'pvp', gameMode: gameMode || 'normal' }, (res: { success: boolean; roomId?: string; playerId?: string; playerName?: string; error?: string }) => {
        if (res.success && res.roomId && res.playerId) {
          resolve({ roomId: res.roomId, playerId: res.playerId });
        } else {
          reject(new Error(res.error || 'Failed to create room'));
        }
      });
    });
  },

  async joinRoom(roomId: string): Promise<JoinRoomResponse> {
    const sock = await getSocket();
    return new Promise((resolve, reject) => {
      sock.emit('join_room', { roomId }, (res: { success: boolean; roomId?: string; playerId?: string; playerName?: string; playerNumber?: number; error?: string }) => {
        if (res.success && res.roomId && res.playerId) {
          resolve({ roomId: res.roomId, playerId: res.playerId, playerNumber: res.playerNumber || 2 });
        } else {
          reject(new Error(res.error || 'Failed to join room'));
        }
      });
    });
  },

  async quickMatch(gameMode?: GameMode): Promise<CreateRoomResponse> {
    const sock = await getSocket();
    return new Promise((resolve, reject) => {
      sock.emit('quick_match', { gameMode: gameMode || 'normal' }, (res: { success: boolean; roomId?: string; playerId?: string; playerName?: string; playerNumber?: number; matched?: boolean; error?: string }) => {
        if (res.success && res.roomId && res.playerId) {
          resolve({ roomId: res.roomId, playerId: res.playerId });
        } else {
          reject(new Error(res.error || 'Quick match failed'));
        }
      });
    });
  },

  async getRoom(roomId: string): Promise<RoomData> {
    // For URL-based join, we use the Socket.IO join_room which handles this
    // This is kept for backward compatibility with the URL join flow
    const res = await api.joinRoom(roomId);
    return {
      id: res.roomId,
      status: 'active',
      difficulty: 'pvp',
      room_participants: [],
    };
  },
};
